import { AUDIT_DEFAULT_PAGE_SIZE, type AuditLog, type AuditSort } from '@/features/users/hooks';

export interface AuditFilters {
  search: string;
  entityType: string;
  entityId: string;
  action: string;
  actorId: string;
  from: string;
  to: string;
  sort: AuditSort;
  page: number;
  pageSize: number;
}

export const DEFAULT_AUDIT_FILTERS: AuditFilters = {
  search: '',
  entityType: '',
  entityId: '',
  action: '',
  actorId: '',
  from: '',
  to: '',
  sort: { column: 'created_at', dir: 'desc' },
  page: 0,
  pageSize: AUDIT_DEFAULT_PAGE_SIZE,
};

export type AuditActorProfile = {
  id: string;
  full_name: string | null;
  email: string;
  avatar_url?: string | null;
  is_active?: boolean;
};

export const ACTION_VARIANT: Record<string, 'default' | 'success' | 'info' | 'warning' | 'muted'> =
  {
    insert: 'success',
    update: 'info',
    delete: 'warning',
  };

/** `profile_role_change` → `Profile role change`. */
export function entityTypeLabel(entityType: string): string {
  const spaced = entityType.replace(/[_-]+/g, ' ').trim();
  if (!spaced) return entityType;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** `insert` → `Created`, `mfa_enroll` → `Mfa enroll`. */
export function actionLabel(action: string): string {
  switch (action) {
    case 'insert':
      return 'Created';
    case 'update':
      return 'Updated';
    case 'delete':
      return 'Deleted';
    default:
      return entityTypeLabel(action);
  }
}

type ActorFields = Pick<AuditLog, 'actor_id' | 'actor_name' | 'actor_email'>;

/**
 * Name to show for the actor. The row's own snapshot wins (it reflects who
 * the person was at the time, and survives a deleted profile); the live
 * profile fills in for rows written before migration 137.
 */
/**
 * True for automated writes (sync jobs, Edge Functions on the service role,
 * triggers with no signed-in user). A row with no actor_id but an identity
 * snapshot is a *deleted* account, not the system.
 */
export function isSystemActor(row: ActorFields): boolean {
  return !row.actor_id && !row.actor_name && !row.actor_email;
}

export function actorDisplayName(row: ActorFields, profile?: AuditActorProfile) {
  if (isSystemActor(row)) return 'System';
  if (row.actor_name) return row.actor_name;
  if (row.actor_email) return row.actor_email;
  if (profile) return profile.full_name ?? profile.email;
  return `Unknown user (${row.actor_id!.slice(0, 8)}…)`;
}

export function actorEmail(row: ActorFields, profile?: AuditActorProfile): string | null {
  return row.actor_email ?? profile?.email ?? null;
}

export function actorInitials(row: ActorFields, profile?: AuditActorProfile): string {
  if (isSystemActor(row)) return 'SYS';
  const source = row.actor_name ?? row.actor_email ?? profile?.full_name ?? profile?.email;
  if (!source) return '?';
  return (
    source
      .split(/[.\s@]/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase() ?? '')
      .join('') || '?'
  );
}

export function aalLabel(aal: string | null): string | null {
  if (aal === 'aal2') return 'MFA verified';
  if (aal === 'aal1') return 'Password only';
  return null;
}

/** Short, human-ish browser / OS summary from a User-Agent string. */
export function summarizeUserAgent(ua: string | null): string | null {
  if (!ua) return null;
  const browser = /Edg\/([\d.]+)/.exec(ua)
    ? 'Edge'
    : /OPR\/([\d.]+)/.exec(ua)
      ? 'Opera'
      : /Chrome\/([\d.]+)/.exec(ua)
        ? 'Chrome'
        : /Firefox\/([\d.]+)/.exec(ua)
          ? 'Firefox'
          : /Safari\/([\d.]+)/.exec(ua) && /Version\/([\d.]+)/.exec(ua)
            ? 'Safari'
            : /Deno|node|undici|Supabase/i.test(ua)
              ? 'Server'
              : null;
  const os = /Windows/.test(ua)
    ? 'Windows'
    : /iPhone|iPad/.test(ua)
      ? 'iOS'
      : /Mac OS X/.test(ua)
        ? 'macOS'
        : /Android/.test(ua)
          ? 'Android'
          : /Linux/.test(ua)
            ? 'Linux'
            : null;
  const parts = [browser, os].filter(Boolean);
  return parts.length ? parts.join(' · ') : ua.slice(0, 40);
}

/**
 * Columns the trigger writes on every update that carry no meaning for a
 * human reviewer. Hidden from the change summary (still visible in raw JSON).
 */
const NOISE_FIELDS = new Set(['updated_at']);

/**
 * Snapshot columns that may hold credentials. The audit table stores the
 * full row, so a `plaid_item` or `integration` update would otherwise show
 * the raw access token on screen. Mask by key name; the raw value stays in
 * the database for anyone who truly needs it.
 */
const SENSITIVE_KEY =
  /(token|secret|password|passwd|api_key|apikey|private_key|authorization|cookie|(^|_)pin(_hash)?($|_))/i;

export function isSensitiveField(key: string): boolean {
  return SENSITIVE_KEY.test(key);
}

export interface FieldChange {
  field: string;
  before: unknown;
  after: unknown;
  changed: boolean;
  sensitive: boolean;
}

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'undefined';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const obj = value as Record<string, unknown>;
  return `{${Object.keys(obj)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`)
    .join(',')}}`;
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== 'object' || typeof b !== 'object') return false;
  return stableStringify(a) === stableStringify(b);
}

/**
 * Field-by-field comparison of the `before` / `after` snapshots. For an
 * insert every field is "changed" (from nothing); for a delete every field
 * is "changed" (to nothing). `updated_at` is dropped from the changed list
 * because it moves on every write.
 */
export function diffAuditRow(row: Pick<AuditLog, 'before' | 'after'>): FieldChange[] {
  const before = (row.before ?? {}) as Record<string, unknown>;
  const after = (row.after ?? {}) as Record<string, unknown>;
  const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)])).sort();
  return keys.map((field) => {
    const b = before[field];
    const a = after[field];
    const changed = !NOISE_FIELDS.has(field) && !valuesEqual(b, a);
    return { field, before: b, after: a, changed, sensitive: isSensitiveField(field) };
  });
}

export function changedFields(row: Pick<AuditLog, 'before' | 'after' | 'action'>): string[] {
  return diffAuditRow(row)
    .filter((c) => c.changed)
    .map((c) => c.field);
}

/** Short inline rendering of a JSON value for table cells. */
export function formatFieldValue(value: unknown, opts?: { sensitive?: boolean }): string {
  if (opts?.sensitive && value != null && value !== '') return '••••••••';
  if (value === undefined) return '—';
  if (value === null) return 'null';
  if (typeof value === 'string') return value === '' ? '""' : value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

/** Pretty JSON with sensitive keys masked, for the raw-snapshot view. */
export function redactedJson(value: unknown): string {
  const mask = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(mask);
    if (v && typeof v === 'object') {
      return Object.fromEntries(
        Object.entries(v as Record<string, unknown>).map(([k, val]) => [
          k,
          isSensitiveField(k) && val != null && val !== '' ? '••••••••' : mask(val),
        ]),
      );
    }
    return v;
  };
  return JSON.stringify(mask(value), null, 2);
}

/**
 * Best-effort human label for the record that was touched. Prefers the
 * `entity_label` the DB derived at write time; falls back to scanning the
 * snapshot for rows written before migration 137.
 */
export function entityDisplayName(
  row: Pick<AuditLog, 'before' | 'after'> & Partial<Pick<AuditLog, 'entity_label'>>,
): string | null {
  if (row.entity_label) return row.entity_label;
  const snap = (row.after ?? row.before ?? null) as Record<string, unknown> | null;
  if (!snap) return null;
  for (const key of [
    'name',
    'title',
    'full_name',
    'display_name',
    'po_number',
    'order_number',
    'number',
    'sku',
    'label',
    'subject',
    'email',
    'file_name',
  ]) {
    const v = snap[key];
    if (typeof v === 'string' && v.trim()) return v;
  }
  return null;
}

export function auditRowsToCsv(
  rows: AuditLog[],
  profilesById: Map<string, AuditActorProfile>,
): string {
  const header = [
    'timestamp_iso',
    'timestamp_local',
    'actor_name',
    'actor_email',
    'actor_role',
    'actor_id',
    'action',
    'entity_type',
    'entity_id',
    'entity_label',
    'changed_fields',
    'ip_address',
    'user_agent',
    'auth_level',
    'session_id',
    'event_id',
  ];
  const escape = (cell: unknown) => {
    const s = cell == null ? '' : String(cell);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = rows.map((r) => {
    const p = r.actor_id ? profilesById.get(r.actor_id) : undefined;
    const d = new Date(r.created_at);
    return [
      d.toISOString(),
      d.toLocaleString('en-US'),
      actorDisplayName(r, p),
      actorEmail(r, p) ?? '',
      r.actor_role ?? '',
      r.actor_id ?? '',
      r.action,
      r.entity_type,
      r.entity_id ?? '',
      entityDisplayName(r) ?? '',
      changedFields(r).join('; '),
      r.ip_address ?? '',
      r.user_agent ?? '',
      r.auth_aal ?? '',
      r.session_id ?? '',
      r.id,
    ]
      .map(escape)
      .join(',');
  });
  return [header.join(','), ...lines].join('\n');
}
